// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: assistant dashboards. The model describes what to show (queries over a data
// source, then panels that draw a query's result); the server fetches the records and
// does the arithmetic, so every number on a card is computed, never typed by the model.
// The same spec is re-run for refresh, a different period, schedules and alerts.

import {z} from 'zod';

export const DASHBOARD_SOURCE_IDS = [
	'clickup_time',
	'clickup_tasks',
	'clickup_status_changes',
	'mail',
	'calendar',
	'whatsapp',
	'konvo_messages',
	'commitments',
	'reminders',
	'automation_runs',
	'assistant_usage',
	'connector',
	'inline',
] as const;
export type DashboardSourceId = (typeof DASHBOARD_SOURCE_IDS)[number];

export const DASHBOARD_PERIOD_PRESETS = [
	'today',
	'yesterday',
	'this_week',
	'last_week',
	'this_month',
	'last_month',
	'this_quarter',
	'last_quarter',
	'this_year',
	'last_year',
	'last_7_days',
	'last_14_days',
	'last_30_days',
	'last_90_days',
	'last_12_months',
	'all_time',
] as const;
export type DashboardPeriodPreset = (typeof DASHBOARD_PERIOD_PRESETS)[number];

export const DASHBOARD_BUCKETS = ['hour', 'day', 'week', 'month', 'quarter', 'year', 'weekday', 'hour_of_day'] as const;
export type DashboardBucket = (typeof DASHBOARD_BUCKETS)[number];

export const DASHBOARD_FILTER_OPS = [
	'eq',
	'neq',
	'in',
	'not_in',
	'contains',
	'not_contains',
	'gt',
	'gte',
	'lt',
	'lte',
	'exists',
	'missing',
] as const;
export type DashboardFilterOp = (typeof DASHBOARD_FILTER_OPS)[number];

export const DASHBOARD_MEASURE_OPS = [
	'count',
	'sum',
	'avg',
	'min',
	'max',
	'median',
	'count_distinct',
	'ratio',
	'share',
] as const;
export type DashboardMeasureOp = (typeof DASHBOARD_MEASURE_OPS)[number];

export const DASHBOARD_PANEL_TYPES = [
	'kpi',
	'bar',
	'line',
	'area',
	'donut',
	'scatter',
	'heatmap',
	'calendar',
	'funnel',
	'gauge',
	'treemap',
	'table',
	'feed',
] as const;
export type DashboardPanelType = (typeof DASHBOARD_PANEL_TYPES)[number];

export const DASHBOARD_FORMATS = ['number', 'integer', 'decimal', 'percent', 'hours', 'minutes', 'currency'] as const;
export type DashboardFormat = (typeof DASHBOARD_FORMATS)[number];

export const DASHBOARD_FIELD_TYPES = ['string', 'number', 'date', 'boolean', 'strings'] as const;
export type DashboardFieldType = (typeof DASHBOARD_FIELD_TYPES)[number];

export const DASHBOARD_ID_PATTERN = /^[a-z0-9]{12,40}$/;
export const DASHBOARD_MAX_ROWS = 500;
export const DASHBOARD_MAX_INLINE_ROWS = 500;
/** Columns a query groups by: a table shows them all, a chart draws two (x and series). */
export const DASHBOARD_MAX_GROUPS = 4;
/** What joins the values of a group's fields into one label ("Customer Services / iCAM"). */
export const DASHBOARD_JOIN_SEPARATOR = ' / ';
/** Records a feed panel lists, newest first. */
export const DASHBOARD_FEED_DEFAULT = 8;
export const DASHBOARD_FEED_MAX = 25;
/** The count every query gets when it names no measures (feeds only list records). */
export const DASHBOARD_DEFAULT_MEASURES = [{as: 'count', op: 'count'}] as const;

const name = z.string().regex(/^[a-z][a-z0-9_]{0,39}$/, 'Use lower_snake_case names');
/** A field path into a record: a name, or dotted for connector JSON ("user.login"). */
const fieldPath = z.string().regex(/^[A-Za-z_][\w.-]{0,79}$/, 'Use a field name from the source');
const day = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Use YYYY-MM-DD');
const scalar = z.union([z.string().max(300), z.number(), z.boolean(), z.null()]);

export const DashboardPeriodSchema = z.union([
	z.object({preset: z.enum(DASHBOARD_PERIOD_PRESETS)}).strict(),
	z.object({start: day, end: day}).strict(),
]);
export type DashboardPeriod = z.infer<typeof DashboardPeriodSchema>;

export const DashboardFilterSchema = z
	.object({
		field: fieldPath,
		op: z.enum(DASHBOARD_FILTER_OPS),
		value: z.union([scalar, z.array(z.union([z.string().max(300), z.number()])).max(100)]).optional(),
	})
	.strict();
export type DashboardFilter = z.infer<typeof DashboardFilterSchema>;

export const DashboardGroupBySchema = z
	.object({
		field: fieldPath,
		bucket: z.enum(DASHBOARD_BUCKETS).optional(),
		/** More fields whose values join this one's in the same column, "Space / List". */
		join: z
			.array(fieldPath)
			.min(1)
			.max(3)
			.optional()
			.describe(
				'More fields shown in the same column: field "space" with join ["list"] gives "Customer Services / iCAM Ticketing".',
			),
		/** Output column name; defaults to the field name. */
		as: name.optional(),
		/** Keep the biggest N groups (by the first measure) and fold the rest into "Other". */
		top: z.number().int().min(1).max(50).optional().describe('Keep the top N groups; the rest become "Other".'),
		label: z.string().trim().min(1).max(60).optional(),
	})
	.strict();
export type DashboardGroupBy = z.infer<typeof DashboardGroupBySchema>;

export const DashboardMeasureSchema = z
	.object({
		as: name,
		op: z.enum(DASHBOARD_MEASURE_OPS),
		/** The field to aggregate; not needed for count, ratio or share. */
		field: fieldPath.optional(),
		/** Only count records matching these (e.g. billable hours beside all hours). */
		where: z
			.array(DashboardFilterSchema)
			.max(6)
			.optional()
			.describe('Only records matching these count for this measure.'),
		/** ratio: numerator / denominator, both earlier measure names. */
		numerator: name.optional(),
		denominator: name.optional(),
		/** share: this group's part of the whole for an earlier measure. */
		of: name.optional(),
		label: z.string().trim().min(1).max(60).optional(),
		format: z.enum(DASHBOARD_FORMATS).optional(),
	})
	.strict();
export type DashboardMeasure = z.infer<typeof DashboardMeasureSchema>;

export const DashboardInlineFieldSchema = z
	.object({
		name: fieldPath,
		type: z.enum(DASHBOARD_FIELD_TYPES),
		label: z.string().trim().min(1).max(60).optional(),
	})
	.strict();

export const DashboardQuerySchema = z
	.object({
		id: name,
		source: z.enum(DASHBOARD_SOURCE_IDS),
		/** Source options, e.g. {people: "everyone", space: "Customer Services"} for clickup_time. */
		params: z
			.record(z.string(), z.unknown())
			.optional()
			.describe('Source options from dashboard_sources, e.g. {"people":"everyone","space":"Customer Services"}.'),
		/** Which date the period applies to, for sources with several (tasks: created, closed, due). "none" ignores the period. */
		date_field: z
			.string()
			.max(40)
			.optional()
			.describe('Date field the period filters on (default: the source default). "none" ignores the period.'),
		/** Overrides the dashboard period for this query. */
		period: DashboardPeriodSchema.optional().describe(
			'Pins this query to its own period; omit to follow the dashboard period.',
		),
		where: z.array(DashboardFilterSchema).max(12).optional(),
		group_by: z
			.array(DashboardGroupBySchema)
			.max(DASHBOARD_MAX_GROUPS)
			.optional()
			.describe('Up to four columns; a table shows them all, a chart draws two (x and series).'),
		measures: z
			.array(DashboardMeasureSchema)
			.min(1)
			.max(6)
			.default(() => DASHBOARD_DEFAULT_MEASURES.map((measure) => ({...measure})))
			.describe('Default: one count measure named "count".'),
		sort: z
			.object({by: name, dir: z.enum(['asc', 'desc']).default('desc')})
			.strict()
			.optional(),
		limit: z.number().int().min(1).max(DASHBOARD_MAX_ROWS).optional(),
		/** Also compute the same numbers for the period before (KPI deltas, comparison series). */
		compare: z
			.enum(['previous_period', 'previous_year'])
			.optional()
			.describe('Also compute the previous period, for KPI deltas.'),
		/** inline only: the records, when the numbers came from something read (a mail, a PDF). */
		rows: z.array(z.record(z.string(), scalar)).max(DASHBOARD_MAX_INLINE_ROWS).optional(),
		fields: z.array(DashboardInlineFieldSchema).max(30).optional(),
	})
	.strict();
export type DashboardQuery = z.infer<typeof DashboardQuerySchema>;

export const DashboardPanelSchema = z
	.object({
		id: name,
		type: z.enum(DASHBOARD_PANEL_TYPES),
		title: z.string().trim().min(1).max(80),
		subtitle: z.string().trim().max(140).optional(),
		query: name,
		width: z
			.enum(['full', 'half', 'third'])
			.optional()
			.describe('Grid width on wide screens. Default: kpi third, other panels full.'),
		/** Category or time column (a group_by output). */
		x: name.optional().describe('The category or time column (a group_by column name).'),
		/** Second dimension: one series (colour) per value. */
		series: name.optional().describe('Second group_by column: one coloured series per value.'),
		/** Measures to draw; defaults to every measure (the first one for single-value panels). */
		y: z.array(name).min(1).max(6).optional().describe('Measures to draw (default: all).'),
		/** kpi and gauge: the measure shown. */
		value: name.optional().describe('kpi and gauge: the measure shown.'),
		stacked: z.boolean().optional(),
		horizontal: z.boolean().optional(),
		format: z.enum(DASHBOARD_FORMATS).optional(),
		currency: z
			.string()
			.regex(/^[A-Z]{3}$/)
			.optional(),
		/** An optional reference line (bar, line, area) or the gauge maximum. */
		target: z
			.object({value: z.number(), label: z.string().trim().max(40).optional()})
			.strict()
			.optional(),
		sort: z.enum(['value_desc', 'value_asc', 'label', 'none']).optional(),
		/** One category to highlight; the rest go grey. */
		highlight: z.string().max(120).optional(),
		/** kpi: a time-bucketed query drawn as a sparkline under the number. */
		trend: name.optional().describe('kpi: id of a query grouped by a time bucket, drawn as a sparkline.'),
		/** table: which columns, in order. */
		columns: z.array(name).max(12).optional(),
		/** feed: more queries whose records join this one's in one timeline, newest first. */
		merge: z
			.array(name)
			.max(6)
			.optional()
			.describe('feed: other query ids whose records join the list, e.g. mail, WhatsApp and tasks in one timeline.'),
		/** feed: how many records to list. */
		limit: z.number().int().min(1).max(DASHBOARD_FEED_MAX).optional().describe('feed: records to list (default 8).'),
	})
	.strict();
export type DashboardPanel = z.infer<typeof DashboardPanelSchema>;

export const DashboardSpecSchema = z
	.object({
		version: z.literal(1).default(1),
		title: z.string().trim().min(1).max(80),
		subtitle: z.string().trim().max(160).optional(),
		/** The period every query uses unless it sets its own. */
		period: DashboardPeriodSchema.default({preset: 'this_month'}),
		queries: z.array(DashboardQuerySchema).min(1).max(12),
		panels: z.array(DashboardPanelSchema).min(1).max(16),
		/** A short takeaway under the panels (Markdown). */
		notes: z.string().trim().max(1000).optional(),
	})
	.strict()
	.superRefine((spec, issue) => {
		const queries = new Map(spec.queries.map((query) => [query.id, query]));
		if (queries.size !== spec.queries.length) issue.addIssue({code: 'custom', message: 'Query ids must be unique'});
		const panelIds = new Set(spec.panels.map((panel) => panel.id));
		if (panelIds.size !== spec.panels.length) issue.addIssue({code: 'custom', message: 'Panel ids must be unique'});
		for (const query of spec.queries) {
			const problem = queryProblem(query);
			if (problem) issue.addIssue({code: 'custom', message: `Query "${query.id}": ${problem}`});
		}
		for (const panel of spec.panels) {
			const query = queries.get(panel.query);
			if (!query) {
				issue.addIssue({code: 'custom', message: `Panel "${panel.id}" uses unknown query "${panel.query}"`});
				continue;
			}
			const problem = panelProblem(panel, query, queries);
			if (problem) issue.addIssue({code: 'custom', message: `Panel "${panel.id}": ${problem}`});
		}
	});
export type DashboardSpec = z.infer<typeof DashboardSpecSchema>;
export type DashboardSpecInput = z.input<typeof DashboardSpecSchema>;

export function groupColumnName(group: DashboardGroupBy): string {
	return group.as ?? group.field.replace(/[^a-z0-9_]/gi, '_').toLowerCase();
}

function queryProblem(query: DashboardQuery): string | null {
	const measures = new Set<string>();
	const groups = (query.group_by ?? []).map(groupColumnName);
	if (new Set(groups).size !== groups.length) return 'group_by columns need distinct names (use "as")';
	for (const group of query.group_by ?? []) {
		if (group.join && group.bucket) return `group_by "${group.field}" cannot both bucket and join`;
	}
	for (const measure of query.measures) {
		if (measures.has(measure.as) || groups.includes(measure.as)) return `the name "${measure.as}" is used twice`;
		if (['sum', 'avg', 'min', 'max', 'median', 'count_distinct'].includes(measure.op) && !measure.field) {
			return `measure "${measure.as}" (${measure.op}) needs a field`;
		}
		if (measure.op === 'ratio' && !(measure.numerator && measure.denominator)) {
			return `ratio "${measure.as}" needs numerator and denominator measures`;
		}
		if (measure.op === 'ratio' && !(measures.has(measure.numerator!) && measures.has(measure.denominator!))) {
			return `ratio "${measure.as}" must come after the measures it divides`;
		}
		if (measure.op === 'share' && !(measure.of && measures.has(measure.of))) {
			return `share "${measure.as}" needs "of", an earlier measure`;
		}
		measures.add(measure.as);
	}
	if (query.sort && !measures.has(query.sort.by) && !groups.includes(query.sort.by)) {
		return `sort.by "${query.sort.by}" is not a column`;
	}
	if (query.source === 'inline' && !query.rows) return 'inline queries need rows';
	if (query.source !== 'inline' && query.rows) return 'only inline queries carry rows';
	return null;
}

function panelProblem(
	panel: DashboardPanel,
	query: DashboardQuery,
	queries: ReadonlyMap<string, DashboardQuery>,
): string | null {
	const groups = (query.group_by ?? []).map(groupColumnName);
	const measures = query.measures.map((measure) => measure.as);
	const columns = [...groups, ...measures];
	if (panel.x && !groups.includes(panel.x))
		return `x "${panel.x}" must be a group_by column (${groups.join(', ') || 'none'})`;
	if (panel.series && !groups.includes(panel.series)) return `series "${panel.series}" must be a group_by column`;
	if (panel.x && panel.series && panel.x === panel.series) return 'x and series must differ';
	for (const measure of panel.y ?? []) {
		if (!measures.includes(measure)) return `y "${measure}" is not a measure (${measures.join(', ')})`;
	}
	if (panel.value && !measures.includes(panel.value)) return `value "${panel.value}" is not a measure`;
	for (const column of panel.columns ?? []) {
		if (!columns.includes(column)) return `column "${column}" is not in the query`;
	}
	if ((panel.merge?.length || panel.limit !== undefined) && panel.type !== 'feed') {
		return 'merge and limit are for feed panels';
	}
	for (const id of panel.merge ?? []) {
		if (id === panel.query) return `merge repeats the panel's own query "${id}"`;
		const merged = queries.get(id);
		if (!merged) return `merge uses unknown query "${id}"`;
		if (merged.source === 'inline') return 'a feed lists records from sources, not inline rows';
	}
	if (panel.type === 'feed' && query.source === 'inline') return 'a feed lists records from sources, not inline rows';
	if (panel.trend) {
		const trend = queries.get(panel.trend);
		if (!trend) return `trend uses unknown query "${panel.trend}"`;
		if (!trend.group_by?.[0]?.bucket) return 'the trend query must group by a time bucket';
	}
	const charted = ['bar', 'line', 'area', 'donut', 'funnel', 'treemap', 'heatmap', 'calendar', 'scatter'];
	if (charted.includes(panel.type) && groups.length === 0) return `a ${panel.type} needs a query with group_by`;
	if (charted.includes(panel.type) && groups.length > 2) {
		return `a ${panel.type} draws at most two group_by columns (x and series); show more in a table, or join fields into one column`;
	}
	if (panel.type === 'heatmap' && groups.length < 2) {
		return 'a heatmap needs two group_by columns';
	}
	if (panel.type === 'calendar' && query.group_by?.[0]?.bucket !== 'day') return 'a calendar needs a day bucket';
	if (panel.type === 'scatter' && measures.length < 2) return 'a scatter needs two measures (x and y)';
	return null;
}

export interface DashboardFieldDef {
	name: string;
	type: DashboardFieldType;
	label: string;
	description?: string;
	format?: DashboardFormat;
}

export interface DashboardResolvedPeriod {
	preset: DashboardPeriodPreset | null;
	/** Inclusive local days; null for all time. */
	start: string | null;
	end: string | null;
	timezone: string;
}

export interface DashboardColumn {
	name: string;
	label: string;
	kind: 'dimension' | 'measure';
	type: DashboardFieldType;
	bucket?: DashboardBucket;
	format?: DashboardFormat;
}

export type DashboardCell = string | number | boolean | null;

export interface DashboardQueryResult {
	id: string;
	columns: Array<DashboardColumn>;
	rows: Array<Array<DashboardCell>>;
	/** The same query over the comparison period, when compare is set. */
	previous?: {rows: Array<Array<DashboardCell>>; period: DashboardResolvedPeriod};
	/** Records that matched the filters. */
	matched: number;
	period: DashboardResolvedPeriod | null;
	/** Coverage and caveats worth showing under the panel ("former members are not included"). */
	notes: Array<string>;
	/** Values the assistant supplied rather than Konvo computing them from a source. */
	inline?: boolean;
	/** The newest matching records, for feed panels (only on queries a feed uses). */
	items?: Array<DashboardDrillItem>;
	error?: string;
}

export interface DashboardData {
	computed_at: string;
	period: DashboardResolvedPeriod;
	queries: Record<string, DashboardQueryResult>;
}

export interface DashboardVersionInfo {
	version: number;
	title: string;
	created_at: string;
}

export interface DashboardView {
	id: string;
	title: string;
	version: number;
	latest_version: number;
	versions: Array<DashboardVersionInfo>;
	saved: boolean;
	pinned: boolean;
	spec: DashboardSpec;
	data: DashboardData;
	created_at: string;
	updated_at: string;
	/** Set when this is a frozen copy someone shared into a chat. */
	share?: {share_id: string; owner_name: string; shared_at: string};
}

export interface DashboardSummary {
	id: string;
	title: string;
	subtitle: string | null;
	version: number;
	saved: boolean;
	pinned: boolean;
	panel_types: Array<DashboardPanelType>;
	pin_order: number;
	created_at: string;
	updated_at: string;
}

export const DASHBOARD_DRILL_REF_KINDS = ['clickup', 'mail', 'whatsapp', 'konvo', 'calendar', 'url'] as const;

export interface DashboardDrillItem {
	title: string;
	subtitle?: string;
	/** A line of what was said: a mail preview, a promise's words. */
	detail?: string;
	/** Where the record came from, for the feed's icon. */
	source?: DashboardSourceId;
	at?: string;
	value?: string;
	ref?: {
		kind: (typeof DASHBOARD_DRILL_REF_KINDS)[number];
		id?: string;
		chat_id?: string;
		channel_id?: string;
		url?: string;
	};
}

export interface DashboardDrillResponse {
	items: Array<DashboardDrillItem>;
	total: number;
}

/** Drill-down target: one mark on one panel, as group column → value. */
export const DashboardDrillRequestSchema = z
	.object({
		panel: name,
		keys: z.record(z.string(), z.union([z.string().max(300), z.number(), z.null()])),
		period: DashboardPeriodSchema.optional(),
		version: z.number().int().min(1).optional(),
	})
	.strict();
export type DashboardDrillRequest = z.infer<typeof DashboardDrillRequestSchema>;

/** The label every "Other" fold uses; the client translates it. */
export const DASHBOARD_OTHER_KEY = '__other__';
/** Group key for a record without a value in that field. */
export const DASHBOARD_NONE_KEY = '__none__';
